import styles from 'styles/modules/Card.module.scss';
import Link from 'next/link';
import Remove from 'components/Svg/Remove';
import SendOutline from 'components/Svg/SendOutline';
import ChevronRight from 'components/Svg/Card/ChevronRight';
import getCharacters from 'helpers/getCharacters';
import Button from 'components/Button';
import tagRemove from 'utils/tag-remove';
import Typo from 'components/Typo';
import formatDate from 'helpers/date';
import EditingService from './Story/EditingService';
import NewsAlertLogo from './Svg/NewsAlertLogo';

const CardOverlay = ({ link }) => {
    if (!link) return null;

    return (
        <Link href={link}>
            <a className={styles.overlayLink} />
        </Link>
    );
};

const CardRemove = ({ onRemove, id }) => {
    return (
        <button className={styles.close} onClick={() => onRemove(id)}>
            <Remove fill={'#C4C4C4'} fillCross={'#FFF'} />
        </button>
    );
};

const CardActionOpen = ({ link }) => {
    return (
        <a className={styles['action']} href={link}>
            <ChevronRight />
        </a>
    );
};

const CardApproval = ({ status, onApprove, id }) => {
    return (
        <Button
            theme={'primary'}
            classes={[styles.button]}
            onClick={() => onApprove(id)}
            title={status === 'pinned' ? 'UNPIN' : 'PIN'}
        />
    );
};

const CardTitle = ({ linkOverlay, title, link }) => {
    if (linkOverlay) {
        return (
            <Typo variant={'h3'} className={['pb-xs', styles.title].join(' ')}>
                {getCharacters(title, 50)}
            </Typo>
        );
    }

    if (link) {
        // External link
        if (link.includes('http')) {
            return (
                <Typo
                    variant={'h3'}
                    Tag={'a'}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className={['pb-xs', styles.title].join(' ')}
                >
                    {getCharacters(title, 50)}
                </Typo>
            );
        }

        // Internal link
        return (
            <Link href={link}>
                <a>
                    <Typo
                        variant={'h3'}
                        Tag={'div'}
                        className={['pb-xs', styles.title].join(' ')}
                    >
                        {getCharacters(title, 50)}
                    </Typo>
                </a>
            </Link>
        );
    }

    return null;
};

const CardBody = ({ body, limit = 300 }) => {
    return (
        <Typo
            variant={'body'}
            className={styles.body}
            dangerouslySetInnerHTML={{
                __html: getCharacters(tagRemove(body), limit),
            }}
        />
    );
};

const CardCta = ({ status, cta, link, onApprove, id }) => {
    if (link.includes('http')) {
        return (
            <div className={styles.wrapperCta}>
                <a
                    className={['mt-xs', styles.cta].join(' ')}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {cta}
                </a>
                {onApprove && (
                    <CardApproval
                        status={status}
                        onApprove={onApprove}
                        id={id}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={styles.wrapperCta}>
            <Link href={link}>
                <a className={['mt-xs', styles.cta].join(' ')}>{cta}</a>
            </Link>
            {onApprove && (
                <CardApproval status={status} onApprove={onApprove} id={id} />
            )}
        </div>
    );
};

const Card = ({
    id,
    pretitle,
    title,
    body,
    publishedAt,
    status,
    cta,
    link,
    linkOverlay,
    onRemove,
    story = false,
    onApprove = null,
    limit = 300,
    alert = false,
    author = null,
    feed = false,
}) => {
    const isNonDefaultStatus = status && status !== 'publish';

    return (
        <div className={[styles.card].join(' ')}>
            <div className={['p-card', styles.cardWrapper].join(' ')}>
                {isNonDefaultStatus && (
                    <div
                        className={[
                            styles['card__status'],
                            status === 'new' ? styles['card__status--new'] : '',
                        ].join(' ')}
                    >
                        <span>{status.toUpperCase()}</span>
                    </div>
                )}
                {linkOverlay && <CardOverlay link={link} />}
                {onRemove && <CardRemove onRemove={onRemove} id={id} />}
                {link && <CardActionOpen link={link} />}
                {alert && (
                    <div className={['mb-xs', styles.logo].join(' ')}>
                        <NewsAlertLogo />
                    </div>
                )}
                {pretitle && (
                    <Typo variant={'caption'} className={'mb-s'}>
                        {pretitle}
                    </Typo>
                )}

                {title && (
                    <CardTitle
                        title={title}
                        linkOverlay={linkOverlay}
                        link={link}
                    />
                )}
                {body && <CardBody body={body} limit={limit} />}
                {cta && link && (
                    <CardCta
                        cta={cta}
                        link={link}
                        onApprove={onApprove}
                        id={id}
                        status={status}
                    />
                )}
            </div>
            <div className={styles['card__actions']}>
                {publishedAt && (
                    <span className={styles['card__status']}>
                        <SendOutline />
                        <span>
                            Published: {formatDate(publishedAt)}
                            {feed && (
                                <>
                                    {', by '}
                                    {alert ? (
                                        <Link
                                            href={
                                                'https://newspage.co.uk/about-us/'
                                            }
                                        >
                                            <a className={styles.authorLink}>
                                                Dominic Hiatt
                                            </a>
                                        </Link>
                                    ) : (
                                        <Link href={`/${author.slug}`}>
                                            <a className={styles.authorLink}>
                                                {author.name}
                                            </a>
                                        </Link>
                                    )}
                                </>
                            )}
                        </span>
                    </span>
                )}
                {story && <EditingService id={id} />}
            </div>
        </div>
    );
};

export default Card;
